import { useState } from "react";
import { Plus, Filter, MoreHorizontal, DollarSign, Calendar, User, Sparkles, Loader2, Phone, Package, History, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDeals, usePipelines, usePipelineStages, useUpdateDeal, useScoreDeal } from "@/hooks/useDeals";
import { Skeleton } from "@/components/ui/skeleton";
import { AddDealDialog } from "@/components/deals/AddDealDialog";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { DealInsightsBadge } from "@/components/ai/DealInsightsBadge";
import { DealTalkingPoints } from "@/components/ai/DealTalkingPoints";
import { DealLineItems } from "@/components/deals/DealLineItems";
import { AIClosingAssistant } from "@/components/ai/AIClosingAssistant";
import { ActivityTimeline } from "@/components/activities/ActivityTimeline";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface DealWithRelations {
  id: string;
  title: string;
  amount: number | null;
  stage_id: string;
  ai_win_probability: number | null;
  created_at: string;
  accounts: { name: string } | null;
  contacts: { first_name: string; last_name: string } | null;
  pipeline_stages: { name: string; color: string | null; position: number; probability?: number | null } | null;
}

function DealCard({ 
  deal, 
  index, 
  onScore, 
  isScoring,
  onOpenTalkingPoints,
  onOpenDetails,
}: { 
  deal: DealWithRelations; 
  index: number;
  onScore: () => void;
  isScoring: boolean;
  onOpenTalkingPoints: () => void;
  onOpenDetails: () => void;
}) {
  const daysInStage = Math.floor(
    (Date.now() - new Date(deal.created_at).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Draggable draggableId={deal.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onOpenDetails}
          className={cn(
            "kanban-card group cursor-pointer",
            snapshot.isDragging && "shadow-lg ring-2 ring-primary/50"
          )}
        >
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-medium text-foreground text-sm leading-tight">
              {deal.title}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mb-3">
            {deal.accounts?.name || "No account"}
          </p>

          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1 text-sm font-semibold text-foreground">
              <DollarSign className="h-3.5 w-3.5 text-success" />
              ${(deal.amount || 0).toLocaleString()}
            </div>
          </div>

          {deal.contacts && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
              <User className="h-3 w-3" />
              <span>
                {deal.contacts.first_name} {deal.contacts.last_name}
              </span>
            </div>
          )}

          {/* AI Insights Badge */}
          <div className="pt-3 border-t border-border/50 space-y-2">
            <DealInsightsBadge deal={deal} />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onScore();
                  }}
                  disabled={isScoring}
                  className="flex items-center gap-1.5 hover:bg-primary/10 rounded px-1.5 py-0.5 transition-colors disabled:opacity-50"
                >
                  {isScoring ? (
                    <Loader2 className="h-3 w-3 text-primary animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 text-primary" />
                  )}
                  <span className="text-xs font-medium text-primary">
                    Re-score
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenTalkingPoints();
                  }}
                  className="flex items-center gap-1.5 hover:bg-accent/10 rounded px-1.5 py-0.5 transition-colors"
                >
                  <Phone className="h-3 w-3 text-accent" />
                  <span className="text-xs font-medium text-accent">
                    Prep Call
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDetails();
                  }}
                  className="flex items-center gap-1.5 hover:bg-cyan-500/10 rounded px-1.5 py-0.5 transition-colors"
                >
                  <Package className="h-3 w-3 text-cyan-500" />
                  <span className="text-xs font-medium text-cyan-500">
                    Items
                  </span>
                </button>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>{daysInStage}d</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default function Deals() {
  const [selectedDeal, setSelectedDeal] = useState<DealWithRelations | null>(null);
  const [detailsDeal, setDetailsDeal] = useState<DealWithRelations | null>(null);
  const [showClosingAssistant, setShowClosingAssistant] = useState(false);
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelines();
  const defaultPipeline = pipelines?.[0];
  const { data: stages, isLoading: stagesLoading } = usePipelineStages(defaultPipeline?.id);
  const { data: deals, isLoading: dealsLoading } = useDeals();
  const updateDeal = useUpdateDeal();
  const scoreDeal = useScoreDeal();

  const isLoading = pipelinesLoading || stagesLoading || dealsLoading;

  const totalValue = (deals || []).reduce(
    (sum, deal) => sum + (Number(deal.amount) || 0),
    0
  );

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStageId = destination.droppableId;
    updateDeal.mutate({ id: draggableId, stage_id: newStageId });
  };

  const handleScoreDeal = (deal: DealWithRelations) => {
    const daysInStage = Math.floor(
      (Date.now() - new Date(deal.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    scoreDeal.mutate({
      id: deal.id,
      title: deal.title,
      amount: deal.amount,
      stage_name: deal.pipeline_stages?.name || "Unknown",
      stage_probability: deal.pipeline_stages?.probability || 0,
      days_in_stage: daysInStage,
      account_name: deal.accounts?.name,
      contact_name: deal.contacts 
        ? `${deal.contacts.first_name} ${deal.contacts.last_name}` 
        : undefined,
      has_proposal: false,
      call_count: 0,
      last_activity_days: daysInStage,
    });
  };

  const handleScoreAllDeals = () => {
    if (!deals || deals.length === 0) {
      toast.error("No deals to score");
      return;
    }

    toast.info(`Scoring ${deals.length} deals...`);
    
    deals.forEach((deal) => {
      const dealWithRelations = deal as DealWithRelations;
      handleScoreDeal(dealWithRelations);
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Deals Pipeline</h1>
            <p className="text-muted-foreground">
              Track and manage your deals across stages
            </p>
          </div>
        </div>
        <div className="flex gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-96 w-72 flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (!stages || stages.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Deals Pipeline</h1>
            <p className="text-muted-foreground">
              Track and manage your deals across stages
            </p>
          </div>
          <AddDealDialog />
        </div>
        <div className="glass rounded-xl p-12 text-center">
          <p className="text-muted-foreground mb-4">
            No pipeline stages configured yet. Create a pipeline to get started.
          </p>
        </div>
      </div>
    );
  }

  const stagesWithDeals = stages.map((stage) => ({
    ...stage,
    deals: (deals || []).filter((d) => d.stage_id === stage.id) as DealWithRelations[],
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deals Pipeline</h1>
          <p className="text-muted-foreground">
            Track and manage your deals across stages
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass rounded-lg px-4 py-2">
            <span className="text-sm text-muted-foreground">Total Value:</span>
            <span className="ml-2 text-lg font-bold text-success">
              ${totalValue.toLocaleString()}
            </span>
          </div>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setShowClosingAssistant(!showClosingAssistant)}
          >
            <Handshake className="h-4 w-4" />
            Closing Assistant
          </Button>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={handleScoreAllDeals}
            disabled={scoreDeal.isPending}
          >
            {scoreDeal.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Score All Deals
          </Button>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filter
          </Button>
          <div data-wizard="create-deal">
            <AddDealDialog />
          </div>
        </div>
      </div>

      {/* AI Closing Assistant Panel */}
      <Collapsible open={showClosingAssistant} onOpenChange={setShowClosingAssistant}>
        <CollapsibleContent className="animate-accordion-down">
          <AIClosingAssistant />
        </CollapsibleContent>
      </Collapsible>

      {/* Kanban Board with Drag and Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stagesWithDeals.map((stage) => (
            <div key={stage.id} className="flex-shrink-0 w-72">
              {/* Column Header */}
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: stage.color || "#06B6D4" }}
                  />
                  <h2 className="font-semibold text-sm">{stage.name}</h2>
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    {stage.deals.length}
                  </span>
                </div>
                <AddDealDialog 
                  defaultStageId={stage.id}
                  trigger={
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Plus className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>

              {/* Droppable Column */}
              <Droppable droppableId={stage.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "kanban-column min-h-[200px] transition-colors",
                      snapshot.isDraggingOver && "bg-primary/5 ring-2 ring-primary/20 ring-inset"
                    )}
                  >
                    {stage.deals.map((deal, index) => (
                      <DealCard 
                        key={deal.id} 
                        deal={deal} 
                        index={index}
                        onScore={() => handleScoreDeal(deal)}
                        isScoring={scoreDeal.isPending}
                        onOpenTalkingPoints={() => setSelectedDeal(deal)}
                        onOpenDetails={() => setDetailsDeal(deal)}
                      />
                    ))}
                    {provided.placeholder}

                    {stage.deals.length === 0 && !snapshot.isDraggingOver && (
                      <div className="flex-1 flex items-center justify-center py-8">
                        <p className="text-sm text-muted-foreground">No deals</p>
                      </div>
                    )}

                    {/* Add Deal Button */}
                    <AddDealDialog
                      defaultStageId={stage.id}
                      trigger={
                        <Button
                          variant="ghost"
                          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground mt-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add deal
                        </Button>
                      }
                    />
                  </div>
                )}
              </Droppable>

              {/* Column Footer */}
              <div className="mt-2 px-1 text-xs text-muted-foreground">
                Total:{" "}
                <span className="font-medium text-foreground">
                  $
                  {stage.deals
                    .reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
                    .toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </DragDropContext>

      {/* Deal Talking Points Dialog */}
      {selectedDeal && (
        <DealTalkingPoints
          deal={selectedDeal}
          isOpen={!!selectedDeal}
          onClose={() => setSelectedDeal(null)}
        />
      )}

      {/* Deal Details Sheet with Line Items */}
      <Sheet open={!!detailsDeal} onOpenChange={(open) => !open && setDetailsDeal(null)}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              {detailsDeal?.title}
            </SheetTitle>
          </SheetHeader>

          {detailsDeal && (
            <div className="mt-6">
              <Tabs defaultValue="items" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="items" className="gap-2">
                    <Package className="h-4 w-4" />
                    Line Items
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="gap-2">
                    <History className="h-4 w-4" />
                    Activity
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="items" className="space-y-6 mt-4">
                  {/* Deal Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/30">
                      <div className="text-sm text-muted-foreground">Deal Value</div>
                      <div className="text-xl font-bold">${(detailsDeal.amount || 0).toLocaleString()}</div>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/30">
                      <div className="text-sm text-muted-foreground">Win Probability</div>
                      <div className="text-xl font-bold">{detailsDeal.ai_win_probability || 0}%</div>
                    </div>
                  </div>

                  {/* Account & Contact */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Account:</span>
                      <span className="font-medium">{detailsDeal.accounts?.name || 'No account'}</span>
                    </div>
                    {detailsDeal.contacts && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Contact:</span>
                        <span className="font-medium">
                          {detailsDeal.contacts.first_name} {detailsDeal.contacts.last_name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Line Items */}
                  <DealLineItems 
                    dealId={detailsDeal.id} 
                    dealTitle={detailsDeal.title}
                    accountName={detailsDeal.accounts?.name}
                    dealAmount={detailsDeal.amount}
                  />
                </TabsContent>
                
                <TabsContent value="activity" className="mt-4">
                  <ActivityTimeline dealId={detailsDeal.id} />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}